// 剣舞の役者の速い連・左右の交互打ちを「全身で斬る」形に作り直した模型（2026-11-03）。
//   作者（11-03 answers/blade-quick-alt-mockup-1103）：motion=fix・tempo=faster。
//   「今は腕だけ振っています。ちゃんと身体全身の動きも含めて作らないとダメ／駄々こねてジタバタしているか…バタバタ横攻撃に見えます」
//   作り＝docs/research/2026-11-03-blade-quick-body-mockup.md。1打ごとに：
//     ① 斬る側の足を1歩前へ出し、当たりの少し前に踏み着く（踏んでから斬る）。着いている足は動かさない。
//     ② 腰が先に回り、肩が後から回り、腕と剣は最後に走る（腰→肩→腕→剣の順に遅れる）。
//     ③ 重心は後ろ足から踏み着いた前足へ移り、当たりで沈む。
//     ④ 振る前に肩を引いて一瞬止める（溜め）。振り抜いた剣は体の向こうの下まで流し、その流れがそのまま次の剣の振りかぶりになる。
//     ⑤ 頭は胴ほど揺らさず、相手から目を外さない。
//   拍：溜め・当たり・戻り・踏み込みは実機の速い連と同じ。1打ごとの間だけ速める案（実機 0.30秒 → 既定 0.22秒）。

import { humanPose, type HumanRig } from '../body'
import { bakeTracks, type MockupMove, type TrackKey, type Tracks } from '../core'

export const BLADE_QUICK_BODY_BEAT = { chargeMs: 700, activeMs: 120, recoveryMs: 700, lungeMs: 180, hits: 4 }
/** 1打ごとの間の案（ms）。既定は 220。答えるページで 260・180 と見比べる。 */
export const BLADE_QUICK_BODY_STEPS = [260, 220, 180] as const
const B = BLADE_QUICK_BODY_BEAT
const LEAD = 400

// 腕の形（二の腕 a・前腕 b・剣 s の角度）。剣は1周ごとに 360 足して、いつも同じ向きに回す（前の上から振り下ろす）。
type Arm = { a: number; b: number; s: number }
const ARM = {
  low: { a: 70, b: 40, s: 30 },         // 立ち：腰の前で剣を下げる
  cross: { a: -100, b: -112, s: 300 },  // 合図（手前の手）：頭の上で2本を X に交差（手前の剣は前上へ）
  crossB: { a: -78, b: -66, s: 240 },   // 合図（奥の手）：奥の剣は後ろ上へ
  coil: { a: -120, b: -210, s: 160 },   // 振りかぶり：手は頭の後ろ、剣は背中へ寝かせる
  deep: { a: -128, b: -215, s: 165 },   // 溜めの底（肩をいちばん引いた所）
  hit: { a: 10, b: 30, s: 405 },        // 当たり：剣は前下 45°
  follow: { a: 70, b: 110, s: 480 },    // 振り抜き：体の向こうの下まで流す
  rise: { a: -20, b: -120, s: 500 },    // 引き上げ途中：手は顔の前（もう片方が斬っている間）
} satisfies Record<string, Arm>

export function makeBladeQuickBody(stepMs: number): MockupMove {
  const S = stepMs
  const T = (i: number) => LEAD + B.chargeMs + i * S
  const lastEnd = T(B.hits - 1) + B.activeMs
  const durMs = lastEnd + B.recoveryMs + 480

  // 足先（床の上の x）。手前の足が1・3発目、奥の足が2・4発目で前へ出る（歩み足・歩幅 18・最初は踏み込みで 38）。
  const stepX: [number, number][] = [] // [足を上げる前の x, 踏み着く x]
  let fFx = 6, fBx = -4
  for (let i = 0; i < B.hits; i++) {
    const near = i % 2 === 0, from = near ? fFx : fBx, to = i === 0 ? 34 : (near ? fBx : fFx) + 18
    stepX.push([from, to]); if (near) fFx = to; else fBx = to
  }
  const pxAt = (i: number) => { // その打ちの後の腰の位置：後ろ足から前足へ6割
    const front = stepX[i]![1], rear = i === 0 ? -4 : stepX[i - 1]![1]
    return rear + 0.62 * (front - rear)
  }

  const tr: Tracks = { px: [], py: [], lean: [], tw: [], ht: [], aF: [], bF: [], sF: [], aB: [], bB: [], sB: [], fFx: [], fFy: [], fBx: [], fBy: [] }
  const k = (name: string, at: number, v: number, e?: TrackKey[2]) => tr[name]!.push([at, v, e])
  const arm = (side: 'F' | 'B', at: number, a: Arm, turn: number, e?: TrackKey[2]) => {
    k('a' + side, at, a.a, e); k('b' + side, at, a.b, e); k('s' + side, at, a.s + 360 * turn, e)
  }

  // 構えと合図（頭の上で2本を交差・低く沈んで重心は後ろ）
  const t0 = T(0)
  k('px', 0, 0); k('px', LEAD + 120, -4); k('px', t0 - B.lungeMs, -6)
  k('py', 0, -73); k('py', LEAD + 120, -69); k('py', t0 - B.lungeMs, -67)
  k('lean', 0, 3); k('lean', LEAD + 120, -4); k('lean', t0 - B.lungeMs, -6)
  k('tw', 0, 0); k('tw', LEAD + 120, -10); k('tw', t0 - B.lungeMs, -14)
  k('ht', 0, 0); k('ht', LEAD + 120, -6); k('ht', t0 - B.lungeMs, -10)
  arm('F', 0, ARM.low, 0); arm('F', LEAD + 120, ARM.cross, 0); arm('F', t0 - B.lungeMs, { ...ARM.cross, a: -106 }, 0)
  arm('B', 0, { a: 80, b: 50, s: 20 }, 0); arm('B', LEAD + 120, ARM.crossB, 0); arm('B', t0 - B.lungeMs, { ...ARM.crossB, a: -72 }, 0)
  k('fFx', 0, 6); k('fFy', 0, 0); k('fBx', 0, -4); k('fBy', 0, 0)

  for (let i = 0; i < B.hits; i++) {
    const near = i % 2 === 0, sg = near ? 1 : -1, X = near ? 'F' : 'B', Y = near ? 'B' : 'F', t = T(i)
    const turn = Math.floor(i / 2) // 斬る腕の何周目か
    const first = i === 0
    // ① 足：斬る側の足を上げて前へ運び、当たりの 0.12 拍前に踏み着く
    const [from, to] = stepX[i]!, foot = 'f' + X, lift = first ? t - B.lungeMs : t - 0.8 * S
    const land = t - 0.12 * S
    k(foot + 'x', lift, from); k(foot + 'x', land, to, 'io')
    k(foot + 'y', lift, 0); k(foot + 'y', (lift + land) / 2, -9, 'out'); k(foot + 'y', land, 0, 'in')
    // ③ 重心：踏み着くまでに前へ・当たりで沈み、振り抜きで前足に乗りきる
    const px0 = pxAt(i)
    k('px', t, px0 - 3, 'io'); k('px', t + 0.35 * S, px0, 'out')
    if (!first) k('py', t - 0.5 * S, -72, 'io')
    k('py', t - 0.3 * S, -71, 'io'); k('py', t, -66, 'in'); k('py', t + 0.35 * S, -67, 'out')
    k('lean', t - 0.3 * S, 4, 'io'); k('lean', t, 13, 'in'); k('lean', t + 0.35 * S, 16, 'out')
    // ② ひねり：溜めの底で肩はいちばん引き、腰はもう回り始めている（腰が先）。当たりで肩が追い越し、振り抜きで回りきる
    k('tw', t - 0.3 * S, -42 * sg, 'io'); k('tw', t, 26 * sg, 'in'); k('tw', t + 0.35 * S, 40 * sg, 'out')
    k('ht', t - 0.3 * S, -4 * sg, 'io'); k('ht', t, 22 * sg, 'in'); k('ht', t + 0.35 * S, 24 * sg, 'out')
    // ④ 斬る腕：振りかぶり→溜めの底→（加速）当たり→（減速）振り抜き
    if (!first) arm(X, t - 0.45 * S, ARM.coil, turn, 'io')
    else arm(X, t - 0.55 * B.lungeMs, ARM.coil, turn, 'io')
    arm(X, t - 0.3 * S, ARM.deep, turn, 'io'); arm(X, t, ARM.hit, turn, 'in'); arm(X, t + 0.35 * S, ARM.follow, turn, 'out')
    // もう片方の腕：斬っている間に顔の前へ引き上げ、次の振りかぶりへ（最初の打ちでは合図から直接）
    if (!first && i < B.hits - 1) arm(Y, t, ARM.rise, Math.floor((i - 1) / 2), 'io')
  }

  // 戻り：後ろ足を前足へ引き寄せ、体を起こしてひねりを戻し、剣を下ろして残心
  const end = T(B.hits - 1) + 0.35 * S, rear = 'fF', [, front] = stepX[B.hits - 1]!, [, rearX] = stepX[B.hits - 2]!
  k(rear + 'x', end + 40, rearX); k(rear + 'x', end + 230, front - 6, 'io')
  k(rear + 'y', end + 40, 0); k(rear + 'y', end + 135, -6, 'out'); k(rear + 'y', end + 230, 0, 'in')
  const pxEnd = front - 3
  k('px', end + 260, pxEnd, 'io'); k('py', end + 320, -73, 'io'); k('lean', end + 320, 4, 'io')
  k('tw', end + 320, 0, 'io'); k('ht', end + 280, 0, 'io')
  arm('F', end + 380, { ...ARM.low, s: ARM.low.s + 720 }, 0, 'io'); arm('B', end + 380, { a: 80, b: 50, s: 20 + 720 }, 0, 'io')
  for (const name in tr) { tr[name]!.sort((a, b) => a[0] - b[0]); tr[name]!.push([durMs, tr[name]!.at(-1)![1]]) }

  const keys = bakeTracks(tr, durMs, v => humanPose({ ...v, fF: [v.fFx, v.fFy], fB: [v.fBx, v.fBy] } as unknown as HumanRig))
  const sec = (S / 1000).toFixed(2)
  return {
    id: `blade-quick-body-${S}`, boss: '剣舞の役者', label: `速い連・全身で斬る交互打ち（1打 ${sec}秒）`, body: 'human-dual-dress',
    durMs, scale: 0.8, baseX: 332, heroAt: 192, keys, interp: 'linear',
    phases: [
      { from: 0, to: LEAD, label: '構え', kind: 'rest' },
      { from: LEAD, to: t0 - B.lungeMs, label: '合図：頭の上で2本を交差・重心を後ろへ', kind: 'tell' },
      { from: t0 - B.lungeMs, to: t0, label: '踏み込み：手前の足を大きく出して肩を引く', kind: 'tell' },
      { from: t0, to: lastEnd, label: `手前・奥・手前・奥（1打ごとに踏み替え・約${sec}秒ごと）`, kind: 'hit' },
      { from: lastEnd, to: durMs, label: '戻り：足を引き寄せて残心', kind: 'rest' },
    ],
    hits: Array.from({ length: B.hits }, (_, i) => T(i)), activeMs: B.activeMs,
    box: [22, 100, -150, -40], paths: ['sF', 'sB'], fadeOutMs: 300,
    source: `溜め 0.70秒・当たり 0.12秒・戻り 0.70秒は実機の速い連と同じ。1打ごとの間だけ実機の 0.30秒 → ${sec}秒（作者 11-03「もっと速く」）`,
  }
}

export const BLADE_QUICK_BODY_MOCKUPS = BLADE_QUICK_BODY_STEPS.map(makeBladeQuickBody)
/** 既定（1打 0.22秒） */
export const BLADE_QUICK_BODY_MOCKUP = BLADE_QUICK_BODY_MOCKUPS[1]!
